import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VSelect,
  VTextarea,
} from "@/components/ui";
import { SEED_USERS } from "@/domains/auth";
import { useAddCustomTask, useDeleteTask, useUpdateTask } from "@/domains/tasks/api/mutations";
import { TASK_TEMPLATE_OPTIONS } from "@/domains/tasks/components/taskTemplateIcon";
import type {
  CameraTask,
  TaskAssigneeKind,
  TaskCategory,
  TaskStatus,
} from "@/domains/tasks/api/types";

const CATEGORIES: ReadonlyArray<{ value: TaskCategory; label: string }> = [
  { value: "placement", label: "Plaatsing" },
  { value: "operations", label: "Operaties" },
  { value: "external", label: "Externe partner" },
  { value: "administration", label: "Administratie" },
  { value: "decommission", label: "Ophaling" },
];

const STATUSES: ReadonlyArray<{ value: TaskStatus; label: string }> = [
  { value: "todo", label: "Te doen" },
  { value: "inProgress", label: "Bezig" },
  { value: "done", label: "Klaar" },
  { value: "blocked", label: "Geblokkeerd" },
];

function parseCategory(value: string): TaskCategory {
  return CATEGORIES.find((c) => c.value === value)?.value ?? "administration";
}

function parseStatus(value: string): TaskStatus {
  return STATUSES.find((s) => s.value === value)?.value ?? "todo";
}

type Mode = { kind: "create"; caseId: string; parentId?: string } | { kind: "edit"; task: CameraTask };

type TaskFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  defaultDueDate?: string;
};

type FormState = {
  title: string;
  description: string;
  category: TaskCategory;
  templateId: string;
  assigneeUserId: string;
  dueDate: string;
  reminderDate: string;
  status: TaskStatus;
};

function initialState(mode: Mode, defaultDueDate: string): FormState {
  if (mode.kind === "edit") {
    return {
      title: mode.task.title,
      description: mode.task.description ?? "",
      category: mode.task.category,
      templateId: mode.task.templateId ?? "tpl-custom",
      assigneeUserId: mode.task.assignee?.userId ?? "",
      dueDate: mode.task.dueDate,
      reminderDate: mode.task.reminderDate ?? "",
      status: mode.task.status,
    };
  }
  return {
    title: "",
    description: "",
    category: "administration",
    templateId: "tpl-custom",
    assigneeUserId: "u-stephanie",
    dueDate: defaultDueDate,
    reminderDate: "",
    status: "todo",
  };
}

function kindForUserId(userId: string): TaskAssigneeKind {
  const user = SEED_USERS.find((u) => u.id === userId);
  if (!user) return "internal";
  if (user.role === "partner") return "partner";
  if (user.role === "requester") return "requester";
  return "internal";
}

export function TaskFormDialog({
  open,
  onOpenChange,
  mode,
  defaultDueDate = "2026-05-11",
}: TaskFormDialogProps): React.JSX.Element {
  const [state, setState] = useState<FormState>(() => initialState(mode, defaultDueDate));
  const addTask = useAddCustomTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const patch = (next: Partial<FormState>): void => setState((prev) => ({ ...prev, ...next }));

  const handleSubmit = (): void => {
    const assigneeUser = SEED_USERS.find((u) => u.id === state.assigneeUserId);
    const assignee = assigneeUser
      ? {
          kind: kindForUserId(state.assigneeUserId),
          userId: assigneeUser.id,
          name: `${assigneeUser.firstName} ${assigneeUser.lastName}`,
        }
      : undefined;

    if (mode.kind === "create") {
      addTask.mutate(
        {
          caseId: mode.caseId,
          title: state.title.trim(),
          description: state.description.trim() || undefined,
          category: state.category,
          status: "todo",
          assignee,
          dueDate: state.dueDate,
          reminderDate: state.reminderDate || undefined,
          parentId: mode.parentId,
          templateId: state.templateId,
        },
        { onSuccess: () => onOpenChange(false) },
      );
      return;
    }

    updateTask.mutate(
      {
        id: mode.task.id,
        patch: {
          title: state.title.trim(),
          description: state.description.trim() || undefined,
          category: state.category,
          status: state.status,
          assignee,
          dueDate: state.dueDate,
          reminderDate: state.reminderDate || undefined,
          templateId: state.templateId,
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const handleDelete = (): void => {
    if (mode.kind !== "edit") return;
    if (!confirm("Taak en eventuele subtaken verwijderen?")) return;
    deleteTask.mutate(mode.task.id, { onSuccess: () => onOpenChange(false) });
  };

  const isCreate = mode.kind === "create";
  const isPending = addTask.isPending || updateTask.isPending || deleteTask.isPending;
  const canSubmit = state.title.trim().length > 0 && state.dueDate.length > 0;

  return (
    <VDialog open={open} onOpenChange={onOpenChange}>
      <VDialogContent
        size="lg"
        title={
          isCreate
            ? mode.parentId
              ? "Nieuwe subtaak"
              : "Nieuwe taak"
            : "Taak bewerken"
        }
        description={
          isCreate
            ? "Geef de details van de taak in. Velden met * zijn verplicht."
            : `Pas titel, deadline, reminder of toegewezen persoon aan. Subtaken blijven gekoppeld.`
        }
      >
        <div className="space-y-3">
          <VField label="Titel" htmlFor="title" required>
            <VInput
              id="title"
              value={state.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="Bv. Camera plaatsen op locatie"
            />
          </VField>

          <VField label="Omschrijving" htmlFor="description">
            <VTextarea
              id="description"
              rows={2}
              value={state.description}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </VField>

          <div className="grid gap-3 sm:grid-cols-2">
            <VField
              label="Type"
              htmlFor="templateId"
              hint="Bepaalt het icoon dat bij de taak hoort."
            >
              <VSelect
                id="templateId"
                value={state.templateId}
                onChange={(e) => patch({ templateId: e.target.value })}
              >
                {TASK_TEMPLATE_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </VSelect>
            </VField>
            <VField label="Categorie" htmlFor="category">
              <VSelect
                id="category"
                value={state.category}
                onChange={(e) => patch({ category: parseCategory(e.target.value) })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </VSelect>
            </VField>
            <VField label="Toegewezen aan" htmlFor="assignee">
              <VSelect
                id="assignee"
                value={state.assigneeUserId}
                onChange={(e) => patch({ assigneeUserId: e.target.value })}
              >
                <option value="">— Geen —</option>
                {SEED_USERS.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.organisation})
                  </option>
                ))}
              </VSelect>
            </VField>
            <VField label="Deadline" htmlFor="dueDate" required>
              <VInput
                id="dueDate"
                type="date"
                value={state.dueDate}
                onChange={(e) => patch({ dueDate: e.target.value })}
              />
            </VField>
            <VField label="Reminder" htmlFor="reminderDate" hint="Optioneel">
              <VInput
                id="reminderDate"
                type="date"
                value={state.reminderDate}
                onChange={(e) => patch({ reminderDate: e.target.value })}
              />
            </VField>
            {!isCreate ? (
              <VField label="Status" htmlFor="status" className="sm:col-span-2">
                <VSelect
                  id="status"
                  value={state.status}
                  onChange={(e) => patch({ status: parseStatus(e.target.value) })}
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </VSelect>
              </VField>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
          {!isCreate ? (
            <VButton
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isPending}
              icon={<Trash2 className="size-4 text-error-600" aria-hidden />}
              className="text-error-700"
            >
              Verwijder
            </VButton>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <VButton variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </VButton>
            <VButton onClick={handleSubmit} disabled={!canSubmit || isPending}>
              {isCreate ? "Toevoegen" : "Opslaan"}
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
